import { mapAliasesAndMetrics } from '@fabric-ui/utils';
import { EntitiesLabels } from '@utils/entities.utils';
import { getAssetScoreField } from '@views/Tickets/ticket.types';

export type BaseSqlParams = {
  fields: string[];
  headCellsFields?: string[];
  visibleHeadCellIds?: string[];
  where?: string;
  isExport?: boolean;
  metrics?: { systemName: string }[];
  calculatedDims?: { systemName: string }[];
  enableExceptions?: boolean;
  isNewRiskScoreEnabled?: boolean;
};

const ASSET_FINDINGS_BREAKDOWN_SELECTS = [
  'critical_active_findings_granular AS CRITICAL',
  'high_active_findings_granular AS HIGH',
  'medium_active_findings_granular AS MEDIUM',
  'low_active_findings_granular AS LOW',
  'info_active_findings_granular AS NONE',
] as const;

const ASSET_FINDINGS_BREAKDOWN_SELECTS_SQL = ASSET_FINDINGS_BREAKDOWN_SELECTS.join(',\n                ');

const getAssetExportComputedFields = (visibleHeadCellIds: string[], isNewRiskScoreEnabled = false) => {
  const includeSeverityScore = visibleHeadCellIds.includes(mapAliasesAndMetrics.severityScore.alias);
  const includeTotalFindings = visibleHeadCellIds.includes(mapAliasesAndMetrics.totalFindings.alias);
  const severityScoreMetric = getAssetScoreField(isNewRiskScoreEnabled);

  return [
    includeSeverityScore ? `${severityScoreMetric} as ${mapAliasesAndMetrics.severityScore.alias}` : null,
    includeTotalFindings ? `${mapAliasesAndMetrics.totalFindings.metric} as ${mapAliasesAndMetrics.totalFindings.alias}` : null,
    ...(includeTotalFindings ? ASSET_FINDINGS_BREAKDOWN_SELECTS : []),
  ].filter(Boolean);
};

export const baseTicketSql = ({
  fields = [],
  where = '',
  isExport = false,
  metrics = [],
  calculatedDims = [],
  enableExceptions,
}: BaseSqlParams) => `select  
                  ${
                    isExport
                      ? fields.join(', ')
                      : `ticket.created as created
                  ${fields.includes('ticket._key') ? '' : ', ticket._key'} 
                  ${fields.includes('ticket.type') ? '' : ', ticket.type'} 
                  ${fields.includes('ticket.integration_info.key') ? '' : ', ticket.integration_info.key'} 
                  ${fields.includes('ticket.state') ? '' : ', ticket.state'} 
                  ${
                    enableExceptions && !isExport
                      ? ', IF(COUNT_DISTINCT_IF(exception._key, exception_status_bucket.closed != true) > 0, true, false) AS hasOpenExceptions'
                      : ''
                  }
                  ${fields.length ? `, ${fields.join(', ')}` : ''}`
                  }
                 ${fields.length && metrics?.length ? ', ' : ''} ${metrics
                   ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                   .join(', ')}
                 ${(fields.length || metrics?.length) && calculatedDims?.length ? ', ' : ''} ${calculatedDims
                   ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                   .join(', ')}
                                                                                                        from tickets 
                  ${where}`;

export const baseIncidentSql = ({ fields = [], where = '', isExport = false, metrics = [], calculatedDims = [] }: BaseSqlParams) => `select 
                ${
                  isExport
                    ? fields.join(', ')
                    : `incident.created as created
                ${fields.includes('incident._key') ? '' : ', incident._key'} 
                ${fields.includes('incident.type') ? '' : ', incident.type'} 
                ${fields.includes('incident.integration_info.key') ? '' : ', incident.integration_info.key'} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
                }
                  ${fields.length && metrics?.length ? ', ' : ''} ${metrics
                    ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                    .join(', ')}
                  ${(fields.length || metrics?.length) && calculatedDims?.length ? ', ' : ''} ${calculatedDims
                    ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                    .join(', ')}
                from incidents
                ${where}`;

export const baseAssetSql = ({
  fields = [],
  where = '',
  headCellsFields = [],
  visibleHeadCellIds = [],
  isExport = false,
  metrics = [],
  calculatedDims = [],
  isNewRiskScoreEnabled = false,
}: BaseSqlParams) => {
  const severityScoreMetric = getAssetScoreField(isNewRiskScoreEnabled);
  const exportComputedAssetFields = getAssetExportComputedFields(visibleHeadCellIds, isNewRiskScoreEnabled);
  const exportFields = [...new Set([...fields, ...exportComputedAssetFields])];

  return `select
                ${
                  isExport
                    ? exportFields.join(', ')
                    : `asset.created as created,
                ${severityScoreMetric} as ${mapAliasesAndMetrics.severityScore.alias},
                ${mapAliasesAndMetrics.totalFindings.metric} as ${mapAliasesAndMetrics.totalFindings.alias},
                ${ASSET_FINDINGS_BREAKDOWN_SELECTS_SQL}
                ${fields.includes('asset._key') ? '' : ', asset._key'}
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
                }
                ${fields.length && metrics?.length ? ', ' : ''} ${metrics
                  ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                  .join(', ')}
                ${(fields.length || metrics?.length) && calculatedDims?.length ? ', ' : ''} ${calculatedDims
                  ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                  .join(', ')}
                from uber_assets
                ${where}
                ${
                  isExport
                    ? ''
                    : `group by created
                ${headCellsFields.includes('asset._key') ? '' : ', asset._key'}
                ${headCellsFields.includes('asset.type') ? '' : ', asset.type'}
                ${headCellsFields.length ? `, ${headCellsFields.join(', ')}` : ''}`
                }`;
};

export const baseAlertSql = ({ fields = [], where = '', isExport = false, metrics = [], calculatedDims = [] }: BaseSqlParams) => `select
               ${
                 isExport
                   ? fields.join(', ')
                   : `
                alert.created as created,
                alert.severity as severity,
                alert.original_severity as original_severity,
                COLLECT_DISTINCT(mitre_attack.tactic) as mitre_tactics,
                COLLECT_DISTINCT(mitre_attack.technique) as mitre_techniques
                ${fields.includes('alert._key') ? '' : ', alert._key'} 
                ${fields.includes('alert.type') ? '' : ', alert.type'} 
                ${!fields.includes('alert.state') ? ', alert.state' : ''} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
               }
               ${fields.length && metrics.length ? ', ' : ''} ${metrics
                 .map(({ systemName }) => `${systemName} as ${systemName}`)
                 .join(', ')}
               ${(fields.length || metrics?.length) && calculatedDims?.length ? ', ' : ''} ${calculatedDims
                 ?.map(({ systemName }) => `${systemName} as ${systemName}`)
                 .join(', ')}
                from uber_alerts
                ${where}`;

export const baseFindingSql = ({ fields = [], where = '', isExport = false, metrics = [], calculatedDims = [] }: BaseSqlParams) => `select
            ${
              isExport
                ? fields.join(', ')
                : `
            finding.created as created
            ${fields.includes('finding._key') ? '' : ', finding._key'}
            ${fields.includes('finding.type') ? '' : ', finding.type'} 
            ${!fields.includes('finding.state') ? ', finding.state' : ''} 
            ${fields.length ? `, ${fields.join(', ')}` : ''}`
            }
            ${fields.length && metrics?.length ? ', ' : ''} ${metrics?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
            ${(fields.length || metrics?.length) && calculatedDims?.length ? ', ' : ''} ${calculatedDims?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
            from uber_findings
           ${where}`;

export const baseFactorSql = ({ fields = [], where = '', isExport = false, calculatedDims = [] }: BaseSqlParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               factor.created as created, 
               factor.type as type, 
               factor.group_id as groupId, 
               factor.effective_normalized_score as scoreLeft,
               factor_normalized_max_score_granular as scoreRight
                ${fields.includes('factor._key') ? '' : ', factor._key'} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}`
           }
           ${fields.length && calculatedDims?.length ? ', ' : ''} ${calculatedDims?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
           from uber_factors
         ${where}`;

export const basePolicyViolationSql = ({ fields = [], where = '', isExport = false, calculatedDims = [] }: BaseSqlParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               policy_violation.created as created, 
               policy_violation.type as type,
               policy_violation.severity
               ${fields.includes('policy_violation._key') ? '' : ', policy_violation._key'} 
               ${fields.length ? `, ${fields.join(', ')}` : ''}`
           }
           ${fields.length && calculatedDims?.length ? ', ' : ''} ${calculatedDims?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
           from uber_policy_violations
         ${where}`;

export const baseViolationTicketSql = ({ fields = [], where = '', isExport = false, calculatedDims = [] }: BaseSqlParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               violation_ticket.created as created, 
               violation_ticket.type as type,
               violation_ticket.severity
               ${fields.includes('violation_ticket.integration_info.key') ? '' : ', violation_ticket.integration_info.key'},
               violation_remediation_percentage_granular as violation_remediation_percentage_granular,
               total_assets_active_and_inactive as total_assets_granular,
               total_policy_violations_granular as total_policy_violations_granular
               ${fields.length ? `, ${fields.join(', ')}` : ''}`
           }
           ${fields.length && calculatedDims?.length ? ', ' : ''} ${calculatedDims?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
           from violation_tickets
         ${where}`;

export const baseExceptionsSql = ({ fields = [], where = '', isExport = false, calculatedDims = [] }: BaseSqlParams) => `select
           ${
             isExport
               ? fields.join(', ')
               : `
               exception.created as created
                ${fields.includes('exception._key') ? '' : ', exception._key'} 
                ${fields.length ? `, ${fields.join(', ')}` : ''}
                ${fields.includes('exception.integration_info.key') ? '' : ', exception.integration_info.key'}`
           }
           ${fields.length && calculatedDims?.length ? ', ' : ''} ${calculatedDims?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
           from uber_exceptions
         ${where}`;

export const baseIdentityFindingsSql = ({ fields = [], where = '', isExport = false, calculatedDims = [] }: BaseSqlParams) => `select
            ${
              isExport
                ? fields.join(', ')
                : `
            identity_finding._key,
            identity_finding.severity as severity,
            identity_finding.title as title,
            COLLECT_DISTINCT(JSON_OBJECT(name:mitre_attack.tactic, id:mitre_attack.tactic_id)) as mitreTactics,
            COLLECT_DISTINCT(JSON_OBJECT(name:mitre_attack.technique, id:mitre_attack.technique)) as mitreTechniques,
            tenant.name as tenantName,
            affected_identities_identity_findings as ${EntitiesLabels.IDENTITIES},
            affected_assets_identity_findings as ${EntitiesLabels.ASSETS},
            affected_groups_identity_findings as ${EntitiesLabels.GROUPS},
            affected_tenants_identity_findings as ${EntitiesLabels.TENANTS}
             ${
               fields.length
                 ? `, 
             ${fields.join(', ')}`
                 : ''
             }`
            }
            ${fields.length && calculatedDims?.length ? ', ' : ''} ${calculatedDims?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
            from uber_identity_findings
          ${where}`;

export const baseTenantSql = ({ fields = [], where = '', isExport = false, calculatedDims = [] }: BaseSqlParams) => `select ${
  isExport
    ? fields.join(', ')
    : `
tenant._key,
tenant.severity_score,
tenant.severity,
total_identity_findings as totalFindings,
critical_active_identity_findings as CRITICAL,
high_active_identity_findings as HIGH,
medium_active_identity_findings as MEDIUM,
low_active_identity_findings as LOW ${fields.length ? `, ${fields.join(', ')}` : ''}`
}
${fields.length && calculatedDims?.length ? ', ' : ''} ${calculatedDims?.map(({ systemName }) => `${systemName} as ${systemName}`).join(', ')}
                                                      from uber_tenants ${where}`;

export const baseIdentitiesSql = ({ fields = [], where = '', isExport = false }: BaseSqlParams) => `select
            ${
              isExport
                ? fields.join(', ')
                : `user._key,
            total_identity_findings as totalFindings,
            active_groups as groups,
            active_roles as roles,
            active_app_role_assignments as app_roles,
            active_applications as applications,
            total_alerts_granular as total_alerts,
            active_user_assets as total_assets,
            critical_active_identity_findings as CRITICAL,
            high_active_identity_findings as HIGH,
            medium_active_identity_findings as MEDIUM,
            low_active_identity_findings as LOW ${fields.length ? `, ${fields.join(', ')}` : ''}`
            }  from uber_users
          ${where}`;

export const baseIdentitySql = ({ fields = [], where = '', isExport = false }: BaseSqlParams) => `select
            ${isExport ? fields.join(', ') : `identity._key ${fields.length ? `, ${fields.join(', ')}` : ''}`}
          from uber_identities
          ${where}`;
